import { useEffect, useState, useCallback } from 'react';
import { Card, Table, Button, Form, Row, Col, Badge, Alert, Spinner, Modal } from 'react-bootstrap';
import { api } from '../api/client';
import ResetPasswordModal from './ResetPasswordModal';

const STATUSES = ['all', 'pending', 'approved', 'rejected', 'suspended'];

export default function AdminManageTeachers() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [detailId, setDetailId] = useState(null);
  const [resetFor, setResetFor] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.get('/admin/teachers?status=' + status));
    } catch (e) { setMsg({ type: 'danger', text: e.message }); }
    finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, verb) => {
    if (!window.confirm(`${verb === 'approve' ? 'Approve' : 'Reject'} this teacher?`)) return;
    try {
      await api.post(`/admin/teachers/${id}/${verb}`);
      setMsg({ type: 'success', text: `Teacher ${verb}d.` });
      await load();
    } catch (e) { setMsg({ type: 'danger', text: e.message }); }
  };

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.name || '').toLowerCase().includes(q) || (r.email || '').toLowerCase().includes(q);
  });

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Manage Teachers</h3>
        <Button onClick={() => setShowAdd(true)}>+ Add Teacher</Button>
      </div>
      {msg.text && <Alert variant={msg.type} dismissible onClose={() => setMsg({ type: '', text: '' })}>{msg.text}</Alert>}

      <Card className="shadow-sm mb-3">
        <Card.Body>
          <Row>
            <Col md={4}><Form.Control placeholder="Search name or email" value={search} onChange={(e) => setSearch(e.target.value)} /></Col>
            <Col md={3}>
              <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading ? <div className="text-center py-4"><Spinner /></div> : (
        <Table bordered hover responsive className="bg-white">
          <thead className="table-dark">
            <tr><th>Name</th><th>Email</th><th>City</th><th>Exp</th><th>Fee</th><th>Rating</th><th>Reg fee</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={9} className="text-center text-muted">No teachers</td></tr>}
            {filtered.map((t) => (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td>{t.email}</td>
                <td>{t.city || '-'}</td>
                <td>{t.experience_years} yrs</td>
                <td>₹{Number(t.monthly_fee).toFixed(0)}</td>
                <td>{Number(t.avg_rating).toFixed(1)}</td>
                <td>{Number(t.registration_fee_paid) ? <Badge bg="success">paid</Badge> : <Badge bg="warning">unpaid</Badge>}</td>
                <td><StatusBadge status={t.status} /></td>
                <td>
                  <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => setDetailId(t.id)}>View</Button>
                  <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => setResetFor(t)}>Reset pwd</Button>
                  {t.status === 'pending' && (
                    <>
                      <Button size="sm" variant="success" className="me-1" onClick={() => act(t.id, 'approve')}>Approve</Button>
                      <Button size="sm" variant="danger" onClick={() => act(t.id, 'reject')}>Reject</Button>
                    </>
                  )}
                  {t.status === 'approved' && <Button size="sm" variant="outline-danger" onClick={() => act(t.id, 'reject')}>Suspend</Button>}
                  {t.status === 'rejected' && <Button size="sm" variant="outline-success" onClick={() => act(t.id, 'approve')}>Re-approve</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {detailId && <TeacherDetailModal id={detailId} onClose={() => setDetailId(null)} />}
      {resetFor && <ResetPasswordModal user={resetFor} onClose={() => setResetFor(null)} onDone={(text) => { setResetFor(null); setMsg({ type: 'success', text }); }} />}
      {showAdd && <AddTeacherModal onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); load(); setMsg({ type: 'success', text: 'Teacher added.' }); }} />}
    </>
  );
}

function StatusBadge({ status }) {
  const map = { pending: 'warning', approved: 'success', rejected: 'danger', suspended: 'secondary' };
  return <Badge bg={map[status] || 'secondary'}>{status}</Badge>;
}

function TeacherDetailModal({ id, onClose }) {
  const [t, setT] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    api.get(`/admin/teachers/${id}`).then(setT).catch((e) => setErr(e.message));
  }, [id]);
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return (
    <Modal show onHide={onClose} size="lg" centered scrollable>
      <Modal.Header closeButton><Modal.Title>Teacher Detail</Modal.Title></Modal.Header>
      <Modal.Body>
        {err && <Alert variant="danger">{err}</Alert>}
        {!t && !err && <div className="text-center"><Spinner /></div>}
        {t && (
          <>
            <h5>{t.name} <StatusBadge status={t.status} /></h5>
            <p className="text-muted small mb-2">{t.email} · {t.phone || 'no phone'}</p>
            {t.bio && <p>{t.bio}</p>}
            <Row>
              <Col md={6}><dl className="row"><dt className="col-6">Qualifications</dt><dd className="col-6">{t.qualifications || '-'}</dd></dl></Col>
              <Col md={6}><dl className="row"><dt className="col-6">Experience</dt><dd className="col-6">{t.experience_years} years</dd></dl></Col>
              <Col md={6}><dl className="row"><dt className="col-6">City / Locality</dt><dd className="col-6">{t.city || '-'} / {t.locality || '-'}</dd></dl></Col>
              <Col md={6}><dl className="row"><dt className="col-6">Rating</dt><dd className="col-6">{Number(t.avg_rating).toFixed(1)}</dd></dl></Col>
              <Col md={6}><dl className="row"><dt className="col-6">Monthly fee</dt><dd className="col-6">₹{Number(t.monthly_fee).toFixed(0)}</dd></dl></Col>
              <Col md={6}><dl className="row"><dt className="col-6">Demo fee</dt><dd className="col-6">₹{Number(t.demo_fee).toFixed(0)}</dd></dl></Col>
              <Col md={6}><dl className="row"><dt className="col-6">Reg fee</dt><dd className="col-6">{Number(t.registration_fee_paid) ? 'paid' : 'unpaid'}</dd></dl></Col>
            </Row>
            <h6 className="mt-3">Subjects</h6>
            {t.subjects?.length ? <ul>{t.subjects.map((s) => <li key={s.id}>{s.name} <span className="text-muted small">({s.expertise_level})</span></li>)}</ul> : <p className="text-muted small">No subjects listed</p>}
            <h6 className="mt-3">Availability</h6>
            {t.availability?.length ? <ul>{t.availability.map((a, i) => <li key={i}>{days[a.day_of_week]} · {(a.start_time || '').slice(0,5)} – {(a.end_time || '').slice(0,5)}</li>)}</ul> : <p className="text-muted small">No slots listed</p>}
          </>
        )}
      </Modal.Body>
      <Modal.Footer><Button onClick={onClose} variant="secondary">Close</Button></Modal.Footer>
    </Modal>
  );
}

function AddTeacherModal({ onClose, onDone }) {
  const [f, setF] = useState({
    name: '', email: '', phone: '', password: '',
    qualifications: '', experience_years: 0, city: '', locality: '',
    monthly_fee: 0, demo_fee: 0, bio: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      await api.post('/admin/teachers', {
        ...f,
        experience_years: Number(f.experience_years) || 0,
        monthly_fee: Number(f.monthly_fee) || 0,
        demo_fee: Number(f.demo_fee) || 0,
      });
      onDone();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Modal show onHide={onClose} size="lg" centered>
      <Modal.Header closeButton><Modal.Title>Add Teacher</Modal.Title></Modal.Header>
      <Modal.Body>
        {err && <Alert variant="danger">{err}</Alert>}
        <Alert variant="info" className="small py-2">
          Creates an approved teacher account (registration fee waived). Share the login credentials manually.
        </Alert>
        <Row>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Full name *</Form.Label><Form.Control value={f.name} onChange={set('name')} required /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Email *</Form.Label><Form.Control type="email" value={f.email} onChange={set('email')} required /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Phone</Form.Label><Form.Control value={f.phone} onChange={set('phone')} /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Password *</Form.Label><Form.Control type="text" value={f.password} onChange={set('password')} placeholder="min 6 chars" required /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Qualifications</Form.Label><Form.Control value={f.qualifications} onChange={set('qualifications')} /></Form.Group></Col>
          <Col md={3}><Form.Group className="mb-3"><Form.Label>Experience (yrs)</Form.Label><Form.Control type="number" min={0} value={f.experience_years} onChange={set('experience_years')} /></Form.Group></Col>
          <Col md={3}><Form.Group className="mb-3"><Form.Label>City</Form.Label><Form.Control value={f.city} onChange={set('city')} /></Form.Group></Col>
          <Col md={3}><Form.Group className="mb-3"><Form.Label>Locality</Form.Label><Form.Control value={f.locality} onChange={set('locality')} /></Form.Group></Col>
          <Col md={3}><Form.Group className="mb-3"><Form.Label>Monthly fee (₹)</Form.Label><Form.Control type="number" min={0} value={f.monthly_fee} onChange={set('monthly_fee')} /></Form.Group></Col>
          <Col md={3}><Form.Group className="mb-3"><Form.Label>Demo fee (₹)</Form.Label><Form.Control type="number" min={0} value={f.demo_fee} onChange={set('demo_fee')} /></Form.Group></Col>
          <Col md={12}><Form.Group className="mb-3"><Form.Label>Bio</Form.Label><Form.Control as="textarea" rows={2} value={f.bio} onChange={set('bio')} /></Form.Group></Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={busy || !f.name || !f.email || !f.password}>{busy ? 'Creating...' : 'Create'}</Button>
      </Modal.Footer>
    </Modal>
  );
}
